import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const HazardsGuide = lazy(() => import("@/pages/HazardsGuide"));
const News = lazy(() => import("@/pages/News"));
const Shop = lazy(() => import("@/pages/Shop"));
const Product = lazy(() => import("@/pages/Product"));
const NewsArticle = lazy(() => import("@/pages/NewsArticle"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const Contact = lazy(() => import("@/pages/Contact"));
const Login = lazy(() => import("@/pages/Login"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Admin = lazy(() => import("@/pages/Admin"));

const PageFallback = () => <div className="min-h-screen bg-midnight" />;

function getSessionId() {
  let sid = localStorage.getItem("olv_sid");
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(36).slice(2);
    localStorage.setItem("olv_sid", sid);
  }
  return sid;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname,
        page_location: window.location.href,
      });
    }
    const url = process.env.REACT_APP_BACKEND_URL;
    if (!url) return;
    axios
      .post(`${url}/api/track/pageview`, { path: location.pathname, session_id: getSessionId() })
      .catch(() => {});
  }, [location.pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <Suspense fallback={<PageFallback />}>
      {location.hash?.includes("session_id=") ? (
        <AuthCallback />
      ) : (
        <>
          <PageViewTracker />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/hhsrs-guide" element={<HazardsGuide />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsArticle />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:slug" element={<Product />} />
              <Route path="/tenancy-agreement" element={<Product />} />
              <Route path="/contact" element={<Contact />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          </Routes>
        </>
      )}
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LazyMotion features={domAnimation}>
          <AppRoutes />
        </LazyMotion>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
